/**
 * Creates an enum-like object from an array of strings or an object
 * @param input Array of strings or object with string keys
 * @returns Object with key:value pairs where keys equal their values
 * @example
 * // מתוך מערך
 * const Colors = enumify(['RED', 'GREEN', 'BLUE']);
 * // Results in { RED: 'RED', GREEN: 'GREEN', BLUE: 'BLUE' }
 * console.log(Colors.RED); // 'RED'
 *
 * // מתוך אובייקט ישירות
 * interface HttpMethodsType {
 *   GET: string;
 *   POST: string;
 *   PUT: string;
 * }
 * const HttpMethodsObj = { GET: 'get', POST: 'post', PUT: 'put' };
 * const HttpMethods = enumify<keyof HttpMethodsType>(Object.keys(HttpMethodsObj) as Array<keyof HttpMethodsType>);
 * console.log(HttpMethods.POST); // 'POST'
 *
 * // שימוש נכון עם מפתחות אובייקט
 * const UserStatus = { ACTIVE: 'active', INACTIVE: 'inactive' };
 * const UserStatusEnum = enumify<keyof typeof UserStatus>(Object.keys(UserStatus) as Array<keyof typeof UserStatus>);
 *
 * // ניתן להשתמש יחד עם הפונקציה keys
 * const UserStatusKeys = keys(UserStatus);
 * const UserStatusEnum = enumify<typeof UserStatusKeys[number]>(UserStatusKeys);
 *
 * // טוב יותר להשתמש בפונקציה keys כדי לקבל מפתחות טובים יותר
 * export const GlobalSearchUserFilterType = enumify(keys(GlobalSearchUserFilterElements));
 *
 */
export function enumify<K extends string>(input: readonly K[] | Record<string, any>): Record<K, K> {
	// If input is an array
	if (Array.isArray(input)) {
		return input.reduce(
			(result, key) => {
				result[key] = key;
				return result;
			},
			{} as Record<K, K>,
		);
	}

	// If input is an object, we create an enum from its keys
	const keys = Object.keys(input) as K[];
	return keys.reduce(
		(result, key) => {
			result[key] = key;
			return result;
		},
		{} as Record<K, K>,
	);
}

/**
 * יוצר enum מטיפוס מסוים - מאפשר הגנה טיפוסית טובה יותר
 * @param obj אובייקט המקור
 * @returns אובייקט enum מטיפוס של מפתחות האובייקט המקורי
 * @example
 * const UserFilters = { ROLE: 'role', USER_STATUS: 'status' };
 * // יוצרים enum שמכיל את מפתחות האובייקט המקורי בלבד
 * const FilterEnum = enumifyFromObject(UserFilters);
 * // FilterEnum.ROLE זמין, אבל FilterEnum.ROLE1 יגרום לשגיאת קומפילציה
 */
export function enumifyFromObject<T extends Record<string, any>>(obj: T): {[K in keyof T]: K} {
	return Object.keys(obj).reduce(
		(acc, key) => {
			acc[key as keyof T] = key as any;
			return acc;
		},
		{} as {[K in keyof T]: K},
	);
}
